import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BedDouble, Bath, Maximize, MapPin, Check, ArrowLeft, Sofa, CalendarDays, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { SiteLayout } from '../../components/site/SiteLayout';
import { PillLink, PillButton } from '../../components/site/Pill';
import { openSiteChat } from '../../components/SiteChat';
import { INTERIORS, schemeImage } from '../../lib/site';
import { formatRent, bedsLabel, ListingCard, type Listing } from './ListingCard';

export default function SiteListingDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: l, isLoading, isError } = useQuery({ queryKey: ['public-listing', slug], queryFn: async () => (await api.public.get<Listing>(`/public/listings/${slug}`)).data, enabled: !!slug });
  const nearby = useQuery({
    queryKey: ['public-listings', 'nearby', l?.property?.city],
    queryFn: async () => (await api.public.get<Listing[]>('/public/listings', { city: l?.property?.city, perPage: 4 })).data,
    enabled: !!l?.property?.city,
  });

  if (isLoading) return <SiteLayout><div className="site-container py-40 text-center text-ink-400">Loading…</div></SiteLayout>;
  if (isError || !l) {
    return (
      <SiteLayout>
        <div className="site-container py-40 text-center">
          <p className="display text-4xl text-ink-900">This home has just been let.</p>
          <p className="mt-3 text-ink-500">Homes move quickly. Here is what is still available.</p>
          <PillLink to="/rent" className="mt-8">See available homes</PillLink>
        </div>
      </SiteLayout>
    );
  }

  const rent = formatRent(l.advertisedRentMinor, l.advertisedRentCurrency);
  const period = l.rentPeriod === 'YEAR' ? 'year' : 'month';
  const hero = l.media?.[0] ?? schemeImage(l.property?.slug);
  const available = l.availableFrom && new Date(l.availableFrom) > new Date() ? new Date(l.availableFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : 'Now';
  const others = (nearby.data ?? []).filter((n) => n.id !== l.id).slice(0, 3);

  return (
    <SiteLayout>
      <div className="site-container pt-8 lg:pt-12">
        <Link to="/rent" className="inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.18em] text-ink-500 transition hover:text-ink-900"><ArrowLeft className="h-3.5 w-3.5" /> All homes</Link>

        {/* Gallery */}
        <div className="mt-6 grid gap-3 lg:grid-cols-4 lg:grid-rows-2">
          <div className="overflow-hidden rounded-[10px] bg-ink-100 lg:col-span-3 lg:row-span-2"><img src={hero} alt={l.property?.name} className="aspect-[16/10] h-full w-full object-cover" /></div>
          <div className="hidden overflow-hidden rounded-[10px] bg-ink-100 lg:block"><img src={INTERIORS.livingArea} alt="" className="h-full w-full object-cover" loading="lazy" /></div>
          <div className="hidden overflow-hidden rounded-[10px] bg-ink-100 lg:block"><img src={l.unit?.bedrooms === 0 ? INTERIORS.kitchenDetail : INTERIORS.bedroom} alt="" className="h-full w-full object-cover" loading="lazy" /></div>
        </div>

        <div className="mt-12 grid gap-12 lg:grid-cols-3 lg:gap-20">
          <div className="lg:col-span-2">
            <p className="eyebrow">{l.property?.name} · {l.property?.city}</p>
            <h1 className="display mt-4 text-[40px] text-ink-900 sm:text-[52px] lg:text-[64px]">{l.headline}</h1>

            <div className="mt-8 flex flex-wrap gap-2.5">
              <Fact icon={<BedDouble className="h-4 w-4" />} label={bedsLabel(l.unit?.bedrooms)} />
              <Fact icon={<Bath className="h-4 w-4" />} label={`${l.unit?.bathrooms ?? 1} bath`} />
              {l.unit?.areaSqft && <Fact icon={<Maximize className="h-4 w-4" />} label={`${l.unit.areaSqft} sq ft`} />}
              <Fact icon={<Sofa className="h-4 w-4" />} label={l.unit?.isFurnished ? 'Furnished' : 'Unfurnished'} />
              <Fact icon={<CalendarDays className="h-4 w-4" />} label={`Available ${available.toLowerCase() === 'now' ? 'now' : available}`} />
            </div>

            <p className="mt-10 whitespace-pre-line text-[16px] leading-[1.8] text-ink-600">{l.description}</p>

            {!!l.unit?.features?.length && (
              <section className="mt-12">
                <h3 className="display text-[28px] text-ink-900">In the home</h3>
                <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">{l.unit.features.map((a) => <li key={a} className="flex items-center gap-2.5 text-[15px] text-ink-700"><Check className="h-4 w-4 text-bronze-500" />{a}</li>)}</ul>
              </section>
            )}
            {!!l.property?.amenities?.length && (
              <section className="mt-10">
                <h3 className="display text-[28px] text-ink-900">In the building</h3>
                <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">{l.property.amenities.map((a) => <li key={a} className="flex items-center gap-2.5 text-[15px] text-ink-700"><Check className="h-4 w-4 text-bronze-500" />{a}</li>)}</ul>
              </section>
            )}

            <div className="mt-12 flex items-center gap-2 text-[14px] text-ink-500"><MapPin className="h-4 w-4 text-bronze-500" />{l.property?.name}, {l.property?.city}{l.property?.postcode ? `, ${l.property.postcode}` : ''}</div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="surface p-7">
              <div className="flex items-baseline gap-2">
                <span className="display text-[44px] text-ink-900">{rent}</span>
                <span className="text-[14px] text-ink-400">per {period}</span>
              </div>
              <dl className="mt-6 divide-y divide-ink-100 text-[14px]">
                <Row k="Available" v={available} />
                {!!l.depositMinor && <Row k="Deposit" v={formatRent(l.depositMinor, l.depositCurrency ?? l.advertisedRentCurrency)} />}
                <Row k="Minimum term" v={`${l.minTermMonths ?? 12} months`} />
                <Row k="Tenancy" v={l.property?.region === 'AE' ? 'Annual · Ejari registered' : 'Rolling periodic'} />
              </dl>
              <PillLink to={`/rent/${l.slug}/apply`} className="mt-7 w-full">Apply for this home</PillLink>
              <PillButton onClick={openSiteChat} variant="ghost" className="mt-3 w-full">Ask about this home</PillButton>
              <p className="mt-5 flex items-start gap-2 text-[12px] leading-relaxed text-ink-400">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bronze-500" />
                {l.property?.region === 'AE' ? 'Rent quoted annually. Ejari registration is included.' : 'Your deposit is protected with a government-approved scheme within 30 days, as the law requires.'}
              </p>
            </div>
          </aside>
        </div>

        {!!others.length && (
          <section className="mt-24 border-t border-ink-100 pt-16 pb-24">
            <h2 className="display text-[36px] text-ink-900 sm:text-[44px]"><em>More</em> in {l.property?.city}</h2>
            <div className="mt-10 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">{others.map((n, i) => <ListingCard key={n.id} listing={n} index={i + 1} />)}</div>
          </section>
        )}
        {!others.length && <div className="pb-24" />}
      </div>
    </SiteLayout>
  );
}

const Fact = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white px-3.5 py-2 text-[13px] text-ink-700">{icon}{label}</span>
);
const Row = ({ k, v }: { k: string; v: string }) => (
  <div className="flex justify-between py-2.5"><dt className="text-ink-500">{k}</dt><dd className="font-medium text-ink-900">{v}</dd></div>
);
